import axios, { AxiosResponse } from "axios";
import { Dispatch } from "@reduxjs/toolkit";
import {
  setDrinks,
  setFood,
  getCategories,
} from "../redux/slices/ProductState";
import {
  BusinessInfo,
  CampaignSettings,
  Category,
  Combo,
  DiscountPromotion,
  DiscountPromotionPayload,
  Currency,
  Language,
  MenuThemeConfig,
  MediaSource,
  NewComboPayload,
  NewPackage,
  Package,
  PasswordUpdateData,
  ProductFormData,
  ProductInterface,
  ProductResponse,
  PaymentProviderAccount,
  PermissionDefinition,
  RestaurantTableSession,
  RestaurantTable,
  Staff,
  StaffFormData,
  StaffRoleTemplate,
  UpdateComboPayload,
  UpdateMenuThemeConfig,
  UpdatePackagePayload,
} from "../types/common.types";
import { OrderFromDB } from "../redux/slices/types/OrderAndSocketState";
import {
  appendOrders,
  setOrders,
  setOrderPagination,
  setOrderSummary,
  setOrderStatusFilter,
  updateOrders,
  updateOrderStatus,
} from "../redux/slices/OrderSocketState";
import {
  addStaffToState,
  removeStaff,
  setStaffs,
  updateStaffs,
} from "../redux/slices/StaffState";
import { ProductType } from "../components/product";
import { getStaffPermission } from "./xhrHelper";
import { setSelectedProduct, updateXHR } from "../redux/slices/AppState";
import { handleError } from "./errorHandler";
import { authStorage } from "../auth/authStorage";
import { emitAuthEvent } from "../auth/authEvents";
import {
  appendFeedbacks,
  setFeedbacks,
  setLoadingMore,
} from "../redux/slices/FeedbackState";
import { can } from "../auth/permissions";

declare module "axios" {
  export interface AxiosRequestConfig {
    silentPermissionError?: boolean;
  }
}

export enum XHR_STATE {
  NONE,
  REQUESTING,
  FAILED,
  SUCCESS,
}

const hasStaffPermission = (permissions: string[] | undefined, permission: string) => {
  if (!permissions) return true;
  return can(permission);
};

let staffAccessConfigCache:
  | {
      permissions?: PermissionDefinition[];
      templates?: StaffRoleTemplate[];
    }
  | undefined;
let staffPermissionDefinitionsPromise: Promise<PermissionDefinition[]> | undefined;
let staffRoleTemplatesPromise: Promise<StaffRoleTemplate[]> | undefined;

const username = process.env.REACT_APP_BASIC_AUTH_USERNAME;
const password = process.env.REACT_APP_BASIC_AUTH_PASSWORD;
const salt = process.env.REACT_APP_BASIC_AUTH_SALT;

const credentials = window.btoa(`${username}:${password}`);

export const xhrHeaders = {
  ContentType: "application/json",
  Authorization: `Basic ${credentials}${salt}`,
};
export const getProdBaseURL = () => {
  let url = null;
  const baseSite = window.location.origin;
  if (baseSite === process.env.REACT_APP_CLIENT_ORIGIN_TESTING) {
    url = process.env.REACT_APP_API_URL_PROD_TESTING;
  }
  if (baseSite === process.env.REACT_APP_CLIENT_ORIGIN_PROD) {
    url = process.env.REACT_APP_API_URL_PROD;
  }

  return url || "";
};
const environment = process.env.NODE_ENV;
export let baseURL = process.env.REACT_APP_API_URL_DEV;
if (environment !== "development" && getProdBaseURL()) {
  baseURL = getProdBaseURL();
}

export const xhr = axios.create({
  withCredentials: true,
  baseURL: `${baseURL?.replace(/\/$/, "")}/cms`,
  headers: xhrHeaders,
});

let silentPermissionErrorDepth = 0;

export const withSilentPermissionErrors = async <T,>(
  task: () => Promise<T>
): Promise<T> => {
  silentPermissionErrorDepth += 1;
  try {
    return await task();
  } finally {
    silentPermissionErrorDepth = Math.max(0, silentPermissionErrorDepth - 1);
  }
};

xhr.interceptors.request.use(
  (config) => {
    const token = authStorage.getToken();
    config.headers = {
      ...config.headers,
      "X-Bearer-Token": token ? `Bearer ${token}` : "",
    };
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

xhr.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl = error.config?.url || "";
    const isCredentialRequest = [
      "/auth/user/login/password",
      "/auth/forgot-password",
    ].some((path) => requestUrl.includes(path));
    const isLogoutRequest = requestUrl.includes("/auth/user/logout");
    const isSessionRequest = requestUrl.includes("/auth/session");
    const isAuthWorkflowRequest =
      isCredentialRequest ||
      isLogoutRequest ||
      isSessionRequest ||
      requestUrl.includes("/user/verify-email");
    const { response } = error;
    if (!response) {
      if (!isAuthWorkflowRequest) handleError(error);
      return Promise.reject(error);
    }

    const { status, data } = response;
    const errorCode = data?.code as string | undefined;

    const isSessionFailure = [
      "TOKEN_MISSING",
      "TOKEN_EXPIRED",
      "TOKEN_INVALID",
      "AUTH_TYPE_INVALID",
      "ACCOUNT_INACTIVE",
    ].includes(errorCode || "");
    const isLegacyInvalidSession =
      status === 403 &&
      typeof data?.message === "string" &&
      data.message.toLowerCase().includes("invalid session");

    if (
      errorCode === "EMAIL_VERIFICATION_REQUIRED" ||
      data?.isVerified === false
    ) {
      const session = authStorage.getSession();
      if (session) {
        authStorage.updateUser({ ...session.user, isVerified: false });
        emitAuthEvent("verification-required");
      }
    } else if (
      isSessionFailure ||
      isLegacyInvalidSession ||
      (status === 401 && !isCredentialRequest && !isLogoutRequest)
    ) {
      authStorage.clearSession();
      emitAuthEvent("session-expired");
    } else if (
      status === 403 &&
      (error.config?.silentPermissionError || silentPermissionErrorDepth > 0)
    ) {
      return Promise.reject(error);
    } else if (!isAuthWorkflowRequest) {
      handleError(error);
    }

    return Promise.reject(error);
  }
);

const pathResolver = (path: string, params: string[]) => {
  let urlPath = path;
  let delimiter = "?";
  if (path && params.length) {
    params.forEach((param) => {
      if (urlPath.includes("?")) delimiter = "&";
      urlPath = `${urlPath}${delimiter}${param}`;
    });
  }

  return urlPath;
};

export const getAllCategories = (dispatch: Dispatch<any>) => {
  return xhr.get("/categories/allCategories").then((response) => {
    const drinkCategories: Category[] = response.data.drinkCategories;
    const foodCategories: Category[] = response.data.foodCategories;
    dispatch(
      getCategories({ categories: drinkCategories, productType: "Drink" })
    );
    dispatch(
      getCategories({ categories: foodCategories, productType: "Food" })
    );
    return null;
  });
};
// ==================== ALL CATEGORIES =======================

export const getDrinkCategories = async (
  dispatch: Dispatch<any>,
  fetchDrinks = false
) => {
  const path = pathResolver("drink_category", [`withDrinks=${fetchDrinks}`]);
  return xhr
    .get(path)
    .then((response) => {
      const categories = response.data;
      dispatch(getCategories({ categories, productType: "Drink" }));
      return categories;
    })
    .catch((err) => console.log("Axios Error: getDrinkCategories"));
};
export const addProductCategory = async (
  name: string,
  itemType: ProductType
) => {
  const productEndpoint =
    itemType === ProductType.Drink ? "drink_category" : "food_category";
  return await xhr.post(productEndpoint, { name }, { headers: xhrHeaders });
};
export const InsertDrink = async (
  formData: ProductFormData
): Promise<AxiosResponse> => {
  return await xhr.post(`/drink/`, formData, {
    headers: xhrHeaders,
  });
};

export type BulkProductPayloadItem = {
  rowIndex: number;
  productType: "food" | "drink";
  categoryId: string;
  names: Record<string, string>;
  descriptions?: Record<string, string>;
  prices: Record<string, number>;
  inStock: boolean;
  photo: string;
  mediaSource: MediaSource;
};

export type BulkProductResponse = {
  success: boolean;
  created: Array<{
    rowIndex: number;
    productType: "food" | "drink";
    name?: string;
    id?: string;
  }>;
  failed: Array<{
    rowIndex: number;
    productType?: "food" | "drink";
    name?: string;
    errors: string[];
  }>;
  summary: {
    total: number;
    created: number;
    failed: number;
  };
};

export const bulkCreateProducts = async (
  products: BulkProductPayloadItem[],
  defaultLanguage: string
): Promise<AxiosResponse<BulkProductResponse>> => {
  return await xhr.post(
    "product/bulk",
    { products, defaultLanguage },
    { headers: xhrHeaders }
  );
};

export const updateProductCategory = async (
  id: string,
  name: string,
  itemType: ProductType.Drink | ProductType.Food
) => {
  const productEndpoint =
    itemType === ProductType.Drink
      ? `drink_category/${id}`
      : `food_category/${id}`;
  return await xhr.put(productEndpoint, { name }, { headers: xhrHeaders });
};

export const deleteProductCategory = async (
  id: string,
  itemType: ProductType.Drink | ProductType.Food
) => {
  const productEndpoint =
    itemType === ProductType.Drink
      ? `drink_category/${id}`
      : `food_category/${id}`;
  return await xhr.delete(productEndpoint, { headers: xhrHeaders });
};

export const UpdateDrink = async (
  formData: ProductFormData,
  drinkId: string = ""
): Promise<AxiosResponse> => {
  const path = pathResolver(`/drink/${drinkId}`, []);
  return await xhr.put(path, formData);
};
export const getDrinks = (
  trigger: "Loader" | "Helper",
  dispatch: Dispatch<any>,
  categoriesExist?: boolean
): void | Promise<ProductInterface[]> => {
  if (trigger === "Loader") {
    return Promise.resolve().then(() => {
      const path = pathResolver("drink/drinks", []);
      return xhr
        .get(path)
        .then((response) => {
          dispatch(setDrinks(response.data));
          return response.data;
        })
        .then(async (drinks) => {
          if (!categoriesExist) {
            return getDrinkCategories(dispatch, true).then(() => drinks);
          } else {
            return drinks;
          }
        });
    });
  } else {
    xhr.get("drink/drinks", { headers: xhrHeaders }).then((response) => {
      dispatch && dispatch(setDrinks(response.data));
    });
  }
};

export const getDrink = async (
  drinkId: string | undefined,
  dispatch: Dispatch<any>
) => {
  if (!drinkId) return false;
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  return await xhr
    .get(`drink/${drinkId}`)
    .then((response) => {
      dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
      return response.data;
    })
    .catch((error) => {
      dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
      console.error(error);
    });
};

export const deleteDrink = async (drink: ProductInterface) => {
  return await xhr.delete(`drink/${drink.id}`, {
    headers: xhrHeaders,
    data: { photo: drink.productPhoto },
  });
};

// ==================== FOOD =======================

export const getFoodProducts = (
  trigger: "Loader" | "Helper",
  dispatch: Dispatch<any>,
  categoriesExist?: boolean
): void | Promise<any> => {
  if (trigger === "Loader") {
    return xhr
      .get("food/food")
      .then((response: AxiosResponse<ProductResponse>) => {
        const foodProducts = response.data.products;
        if (foodProducts) dispatch(setFood(foodProducts));
        return foodProducts;
      })
      .then(async (food) => {
        if (!categoriesExist) {
          return getFoodCategories(dispatch, true).then(() => food);
        }

        return food;
      });
  } else {
    xhr.get("food/food", { headers: xhrHeaders }).then((response) => {
      dispatch && dispatch(setFood(response.data.products));
    });
  }
};

export const getFood = async (
  foodId: string | undefined,
  dispatch: Dispatch<any>
) => {
  if (!foodId) return false;
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  return await xhr
    .get(`food/${foodId}`)
    .then((response) => {
      dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
      const { product } = response.data;
      dispatch(setSelectedProduct(product));
      return response.data;
    })
    .catch((error) => {
      dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
      console.log(error);
    });
};

export const getFoodCategories = async (
  dispatch: Dispatch<any>,
  fetchFood = false
) => {
  try {
    const path = pathResolver("food_category", [`withFood=${fetchFood}`]);
    const response = await xhr.get(path);
    const categories = response.data.foodCategory;
    dispatch(getCategories({ categories, productType: "Food" }));
  } catch (err) {
    return console.log("Axios Error: getDrinkCategories");
  }
};

export const InsertFood = async (
  formData: ProductFormData
): Promise<AxiosResponse> => {
  return await xhr.post(`/food/`, formData, {
    headers: xhrHeaders,
  });
};
export const UpdateFood = async (
  formData: ProductFormData,
  foodId: string = ""
): Promise<AxiosResponse> => {
  return await xhr.put(`/food/${foodId}`, formData, {
    headers: xhrHeaders,
  });
};
export const deleteFood = async (food: ProductInterface) => {
  return await xhr.delete(`food/${food.id}`, {
    headers: xhrHeaders,
    data: { photo: food.productPhoto },
  });
};

// ==================== SETTINGS =======================

export const updateBusinessInfo = async (data: BusinessInfo) => {
  return await xhr.patch(
    "user/business-info",
    { ...data },
    { headers: xhrHeaders }
  );
};

export const addLanguage = async (languages: Language[]) => {
  return await xhr.post(
    `user/language`,
    { languages },
    { headers: xhrHeaders }
  );
};

export const updateDefaultLanguage = async (iso: string) => {
  return await xhr.post(
    `user/language/default`,
    { iso },
    { headers: xhrHeaders }
  );
};

export const updateCurrency = async (currency: Currency) => {
  return await xhr.post(`user/currency`, { currency }, { headers: xhrHeaders });
};

export const updateBanner = async (bannerId: string) => {
  return await xhr.put(
    "user/brand/banner",
    { bannerId },
    { headers: xhrHeaders }
  );
};

export const addBusinessLogo = async (logo: string) => {
  return await xhr.post("user/brand/logo", { logo }, { headers: xhrHeaders });
};

export const changePassword = async (data: PasswordUpdateData) => {
  return await xhr.put(
    `auth/user/password`,
    { ...data },
    { headers: xhrHeaders }
  );
};

export const deleteUserAccount = async () => {
  try {
    const response = await xhr.delete("/user", { headers: xhrHeaders });
    return response.data;
  } catch (error) {
    console.log("Error deleting user account", error);
    throw error;
  }
};

// ==================== ORDER =======================
type AllOrdersResponse = {
  success: boolean;
  orders: OrderFromDB[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
  };
  summary: {
    activeCount: number;
    deliveredCount: number;
    cancelledCount: number;
    revenue: number;
    coinsIssued: number;
  };
};

export type OrderQueryParams = {
  status?: string;
  from?: string;
  to?: string;
  range?: "today" | "7d" | "30d" | "all";
  page?: number;
  limit?: number;
  sort?: string;
};

export const getOrders = (
  dispatch: Dispatch<any>,
  params: OrderQueryParams = {},
  options: { append?: boolean } = {}
) => {
  const staffPermissions = getStaffPermission();
  if (!hasStaffPermission(staffPermissions, "order_view")) {
    throw new Error("Access denied!");
  }
  return xhr
    .get("order/", { headers: xhrHeaders, params })
    .then((response: AxiosResponse<AllOrdersResponse>) => {
      if (response.data.success) {
        dispatch(setOrderStatusFilter(params.status));
        dispatch(
          options.append
            ? appendOrders(response.data.orders)
            : setOrders(response.data.orders)
        );
        dispatch(setOrderPagination(response.data.pagination));
        dispatch(setOrderSummary(response.data.summary));
      }
      return response.data;
    });
};

export const updateOrder = (
  dispatch: Dispatch<any>,
  id: string,
  status: number
) => {
  const staffPermissions = getStaffPermission();
  if (!hasStaffPermission(staffPermissions, "order_update_status")) {
    throw new Error("Access denied!");
  }

  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));

  xhr
    .patch(`order/${id}`, { status }, { headers: xhrHeaders })
    .then((response) => {
      if (response.data.success) {
        dispatch(updateOrderStatus(response.data.order));
        dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
      }
    });
};

export type AttendantOrderItemPayload = {
  productId: string;
  names: Record<string, string>;
  count: number;
  price?: number;
};

export type CreateAttendantOrderPayload = {
  tableNumber: string;
  tableId?: string;
  tableSessionId?: string;
  segment?: string;
  orderName?: string;
  order: AttendantOrderItemPayload[];
};

export const getTableSessions = async (): Promise<RestaurantTableSession[]> => {
  const response = await xhr.get("order/table-session", {
    headers: xhrHeaders,
  });
  return response.data.sessions || [];
};

export const getTableSession = async (
  sessionId: string
): Promise<RestaurantTableSession> => {
  const response = await xhr.get(`order/table-session/${sessionId}`, {
    headers: xhrHeaders,
  });
  return response.data.session;
};

export const recordTableSessionPayment = async (
  sessionId: string,
  payload: { amountPaid: number; closeIfPaid?: boolean }
): Promise<RestaurantTableSession> => {
  const staffPermissions = getStaffPermission();
  if (!hasStaffPermission(staffPermissions, "table_session_payment_record")) {
    throw new Error("Access denied!");
  }
  const response = await xhr.patch(
    `order/table-session/${sessionId}/payment`,
    payload,
    { headers: xhrHeaders }
  );
  return response.data.session;
};

export const createAttendantOrder = async (
  dispatch: Dispatch<any>,
  payload: CreateAttendantOrderPayload
) => {
  const staffPermissions = getStaffPermission();
  if (!hasStaffPermission(staffPermissions, "table_session_order_create")) {
    throw new Error("Access denied!");
  }

  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));

  try {
    const response = await xhr.post("order/table-session", payload, {
      headers: xhrHeaders,
    });

    if (response.data.success) {
      dispatch(updateOrders(response.data.order));
      dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
    }

    return response.data.order;
  } catch (error) {
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    throw error;
  }
};

export type CreateTablePayload = {
  segmentId: string;
  label: string;
  seats?: number | null;
  sortOrder?: number | null;
};

export type BulkCreateTablePayload = {
  segmentId: string;
  prefix: string;
  start: number;
  end: number;
  seats?: number | null;
};

export const getTables = async (
  segmentId?: string
): Promise<RestaurantTable[]> => {
  const response = await xhr.get("table", {
    params: segmentId ? { segmentId } : undefined,
    headers: xhrHeaders,
  });

  return response.data.tables || [];
};

export const createTable = async (
  payload: CreateTablePayload
): Promise<RestaurantTable> => {
  const response = await xhr.post("table", payload, { headers: xhrHeaders });
  return response.data.table;
};

export const bulkCreateTables = async (
  payload: BulkCreateTablePayload
): Promise<RestaurantTable[]> => {
  const response = await xhr.post("table/bulk", payload, {
    headers: xhrHeaders,
  });
  return response.data.tables || [];
};

export const updateTable = async (
  tableId: string,
  payload: Partial<CreateTablePayload> & { status?: number }
): Promise<RestaurantTable> => {
  const response = await xhr.patch(`table/${tableId}`, payload, {
    headers: xhrHeaders,
  });
  return response.data.table;
};

export const deleteTable = async (
  tableId: string
): Promise<RestaurantTable> => {
  const response = await xhr.delete(`table/${tableId}`, {
    headers: xhrHeaders,
  });
  return response.data.table;
};

// ==================== DASHBOARD =======================

export const addSegment = async (
  clientUsername: string,
  segmentName: string,
  priceMultiplier?: number,
  description?: string,
  photo?: string | null
) => {
  return await xhr.post(
    `/user/add-segment`,
    { clientUsername, segmentName, priceMultiplier, description, photo },
    { headers: xhrHeaders }
  );
};

export const updateSegment = async (segmentId: string, updateData: any) => {
  return await xhr.patch(`/user/segment/${segmentId}`, updateData, {
    headers: xhrHeaders,
  });
};

// ==================== STAFF MANAGEMENT =======================
export const getStaffPermissionDefinitions = async (): Promise<
  PermissionDefinition[]
> => {
  if (staffAccessConfigCache?.permissions) {
    return staffAccessConfigCache.permissions;
  }
  if (!staffPermissionDefinitionsPromise) {
    staffPermissionDefinitionsPromise = xhr
      .get("user/staff/access/permissions", {
        headers: xhrHeaders,
      })
      .then((response) => {
        const permissions = response.data.permissions || [];
        staffAccessConfigCache = { ...staffAccessConfigCache, permissions };
        return permissions;
      })
      .finally(() => {
        staffPermissionDefinitionsPromise = undefined;
      });
  }
  return staffPermissionDefinitionsPromise;
};

export const getStaffRoleTemplates = async (): Promise<StaffRoleTemplate[]> => {
  if (staffAccessConfigCache?.templates) {
    return staffAccessConfigCache.templates;
  }
  if (!staffRoleTemplatesPromise) {
    staffRoleTemplatesPromise = xhr
      .get("user/staff/access/templates", {
        headers: xhrHeaders,
      })
      .then((response) => {
        const templates = response.data.templates || [];
        staffAccessConfigCache = { ...staffAccessConfigCache, templates };
        return templates;
      })
      .finally(() => {
        staffRoleTemplatesPromise = undefined;
      });
  }
  return staffRoleTemplatesPromise;
};

export const getStaffs = (dispatch: Dispatch<any>) => {
  return xhr.get("/user/staff").then((response) => {
    if (response.data.success) {
      dispatch(setStaffs(response.data.staffs));
    }
    return null;
  });
};

export const addStaff = async (
  data: StaffFormData,
  dispatch: Dispatch<any>
) => {
  const response = await xhr.post(
    "user/staff",
    { ...data },
    { headers: xhrHeaders }
  );
  if (response.data.success) {
    const rawStaff = response.data.staff;
    const newStaff: Staff = {
      name: rawStaff.name,
      role: rawStaff.role,
      staffId: rawStaff.staffId,
      permissions: data.permissions,
    };
    dispatch(addStaffToState(newStaff));
  }
  return response.data;
};

export const updateStaff = async (data: Staff, dispatch: Dispatch<any>) => {
  const response = await xhr.put(
    `user/staff/${data.staffId}`,
    { ...data },
    { headers: xhrHeaders }
  );
  if (response.data.success) {
    const rawStaff = response.data.staff;
    const updatedStaff: Staff = {
      name: rawStaff.name,
      role: rawStaff.role,
      staffId: rawStaff.staffId,
      permissions: data.permissions,
    };
    dispatch(updateStaffs(updatedStaff));
  }
  return response.data;
};

export const deleteStaff = async (id: number, dispatch: Dispatch<any>) => {
  const response = await xhr.delete(`user/staff/${id}`, {
    headers: xhrHeaders,
  });
  if (response.data.success) {
    dispatch(removeStaff(id));
  }
  return response.data;
};

//======================================== Media =====================================
export const getMedia = async (mediaType: string, query: string = "") =>
  await xhr.get(`media/${mediaType}${query}`, {
    headers: xhrHeaders,
  });

// Deprecated membership upgrade flow.
export const upgradeMembership = async (membershipPlanId: string) => {
  return await xhr.post(
    "payment/initialize",
    { membershipPlanId },
    { headers: xhrHeaders }
  );
};

export const verifyPayment = async (reference: string) => {
  return await xhr.post(
    "payment/verify",
    { reference },
    { headers: xhrHeaders }
  );
};

export type StripeConnectOnboardingResponse = {
  success: boolean;
  account: PaymentProviderAccount | null;
  onboardingUrl: string;
  expiresAt?: number;
};

export const getStripePaymentProviderAccount = async () => {
  const response = await xhr.get<{
    success: boolean;
    account: PaymentProviderAccount | null;
  }>("payment-provider/stripe", {
    headers: xhrHeaders,
  });

  return response.data.account;
};

export const startStripeConnectOnboarding = async (
  refreshUrl: string,
  returnUrl: string
) => {
  const response = await xhr.post<StripeConnectOnboardingResponse>(
    "payment-provider/stripe/onboarding-link",
    { refreshUrl, returnUrl },
    { headers: xhrHeaders }
  );

  return response.data;
};

export const refreshStripePaymentProviderAccount = async () => {
  const response = await xhr.post<{
    success: boolean;
    account: PaymentProviderAccount | null;
  }>(
    "payment-provider/stripe/status",
    {},
    {
      headers: xhrHeaders,
    }
  );

  return response.data.account;
};

export const disconnectStripePaymentProviderAccount = async () => {
  const response = await xhr.delete<{
    success: boolean;
    account: PaymentProviderAccount | null;
  }>("payment-provider/stripe", {
    headers: xhrHeaders,
  });

  return response.data.account;
};

//======================================== Product Analytics =====================================
export const getProductAnalytics = async (productId: string) => {
  try {
    const response = await xhr.get(`/analytics/${productId}`, {
      headers: xhrHeaders,
    });
    return response.data;
  } catch (error) {
    console.log("Error fetching product analytics", error);
    throw error;
  }
};

export type TodayOrderMetrics = {
  ordersToday: number;
  deliveredOrdersToday: number;
  todayRevenue: number;
};

export const getTodayOrderMetrics = async (): Promise<TodayOrderMetrics> => {
  const response = await xhr.get("/analytics/orders/today", {
    headers: xhrHeaders,
  });
  return response.data.metrics;
};

//======================================== Feedback =====================================

export const getFeedbacks = (
  dispatch: Dispatch<any>,
  page: number = 1,
  limit: number = 100,
  replace: boolean = false
) => {
  if (!replace) dispatch(setLoadingMore(true));

  return xhr
    .get(`/user/feedback?page=${page}&limit=${limit}`)
    .then((response) => {
      if (response.data.success) {
        const feedbacks = response.data.data;
        const {
          averageRating,
          total,
          ratingDistribution,
          page: currentPage,
          limit,
        } = response.data.meta;

        if (replace) {
          dispatch(
            setFeedbacks({
              feedbacks,
              averageRating,
              totalReviews: total,
              ratingDistribution,
              currentPage,
              limit,
              hasMore: feedbacks.length >= limit,
              isLoadingMore: false,
            })
          );
        } else {
          dispatch(
            appendFeedbacks({
              feedbacks,
              currentPage,
              limit,
            })
          );
          dispatch(setLoadingMore(false));
        }
      }
      return null;
    });
};

/////////////////////////////// PACKAGE

export const getSpinPackages = async (): Promise<Package[]> => {
  return xhr.get("/campaign/spin-packages").then((res) => res.data.data);
};

export const createSpinPackage = async (pkg: NewPackage): Promise<Package> => {
  return xhr.post("/campaign/spin-packages", pkg).then((res) => res.data);
};

export const updateSpinPackage = async (
  id: string,
  pkg: UpdatePackagePayload
): Promise<Package> => {
  return xhr.put(`/campaign/spin-packages/${id}`, pkg).then((res) => res.data);
};

export const updateActivePackage = async (
  id: string,
  updateData: { isActive: boolean }
): Promise<Package> => {
  return xhr
    .put(`/campaign/spin-packages/${id}`, updateData)
    .then((res) => res.data);
};

export const deleteSpinPackage = async (id: string): Promise<any> => {
  return xhr.delete(`/campaign/spin-packages/${id}`).then((res) => res.data);
};

//////////////SETTINGS
export const getCampaignSettings = async (): Promise<CampaignSettings> => {
  return xhr.get("/campaign/settings").then((res) => res.data.data);
};

export const updateCampaignSettings = async (
  maxDailyWins: number
): Promise<CampaignSettings> => {
  return xhr
    .put("/campaign/settings", { maxDailyWins })
    .then((res) => res.data);
};

export const getMenuTheme = async (): Promise<MenuThemeConfig> => {
  return xhr.get("/menu-theme").then((res) => res.data.menuTheme);
};

export const updateMenuTheme = async (
  payload: UpdateMenuThemeConfig
): Promise<MenuThemeConfig> => {
  return xhr.put("/menu-theme", payload).then((res) => res.data.menuTheme);
};

////////////////////DISCOUNT PROMOTIONS
export const getDiscountPromotions = async (): Promise<DiscountPromotion[]> => {
  return xhr.get("/promotion/discounts").then((res) => res.data.promotions || []);
};

export const createDiscountPromotion = async (
  promotion: DiscountPromotionPayload
): Promise<DiscountPromotion> => {
  return xhr
    .post("/promotion/discounts", promotion)
    .then((res) => res.data.promotion);
};

export const updateDiscountPromotion = async (
  id: string,
  promotion: Partial<DiscountPromotionPayload>
): Promise<DiscountPromotion> => {
  return xhr
    .put(`/promotion/discounts/${id}`, promotion)
    .then((res) => res.data.promotion);
};

export const deleteDiscountPromotion = async (id: string): Promise<void> => {
  await xhr.delete(`/promotion/discounts/${id}`);
};

////////////////////COMBO
export const getCombos = async (): Promise<Combo[]> => {
  return xhr.get("/combo").then((res) => res.data.data);
};

export const createCombo = async (
  newCombo: NewComboPayload
): Promise<Combo> => {
  return xhr.post("/combo", newCombo).then((res) => res.data.data);
};

export const updateComboItem = async (
  id: string,
  updatedCombo: UpdateComboPayload
): Promise<Combo> => {
  return xhr.put(`/combo/${id}`, updatedCombo).then((res) => res.data.data);
};

export const deleteCombo = async (id: string): Promise<any> => {
  return xhr.delete(`/combo/${id}`).then((res) => res.data);
};
